import {
  apyToApr,
  calculateRateForCompoundType,
  TimeScale,
} from '../../../../utils/parsing'
import { CAction } from './types'

/**
 * Sumer CTokenMetadata struct as returned by the Sumer lens contract.
 */
type SumerCTokenMetadata = {
  cToken?: string
  exchangeRateCurrent?: bigint
  supplyRatePerBlock?: bigint
  borrowRatePerBlock?: bigint
  reserveFactorMantissa?: bigint
  totalBorrows?: bigint
  totalReserves?: bigint
  totalSupply?: bigint
  totalCash?: bigint
  isListed?: boolean
  underlyingAssetAddress?: string
  cTokenDecimals?: bigint
  underlyingDecimals?: bigint
  isCToken?: boolean
  isCEther?: boolean
  borrowCap?: bigint
  depositCap?: bigint
  heteroLiquidationIncentive?: bigint
  homoLiquidationIncentive?: bigint
  sutokenLiquidationIncentive?: bigint
  groupId?: number | bigint
  intraRate?: bigint
  interRate?: bigint
  mintRate?: bigint
  discountRate?: bigint
  interMintAllowed?: boolean
}

/**
 * Safely extracts and normalizes all fields from a Sumer CTokenMetadata object.
 * Returns a plain JS object compatible with the CTokenMeta shape used by publicCallParse.
 */
export function readSumerCTokenMetadata(meta: SumerCTokenMetadata, token: any) {
  return {
    cToken: meta.cToken ?? '',
    exchangeRateCurrent: meta.exchangeRateCurrent ?? 0n,
    supplyRatePerBlock: meta.supplyRatePerBlock ?? 0n,
    borrowRatePerBlock: meta.borrowRatePerBlock ?? 0n,
    reserveFactorMantissa: meta.reserveFactorMantissa ?? 0n,
    totalBorrows: meta.totalBorrows ?? 0n,
    totalReserves: meta.totalReserves ?? 0n,
    totalSupply: meta.totalSupply ?? 0n,
    totalCash: meta.totalCash ?? 0n,
    isListed: meta.isListed ?? true,
    // use intraRate as the default collateralFactorMantissa for backward compat
    collateralFactorMantissa: meta.intraRate ?? 0n,
    underlyingAssetAddress: meta.underlyingAssetAddress ?? '',
    cTokenDecimals: meta.cTokenDecimals ?? 0n,
    underlyingDecimals: meta.underlyingDecimals ?? 0n,
    isCToken: meta.isCToken ?? true,
    isCEther: meta.isCEther ?? false,
    borrowCap: meta.borrowCap ?? 0n,
    depositCap: meta.depositCap ?? 0n,
    // group-based collateral factors
    groupId: Number(meta.groupId ?? 0),
    intraRate: meta.intraRate ?? 0n,
    interRate: meta.interRate ?? 0n,
    mintRate: meta.mintRate ?? 0n,
    discountRate: meta.discountRate ?? 0n,
    interMintAllowed: meta.interMintAllowed ?? false,
    // liquidation incentives
    heteroLiquidationIncentive: meta.heteroLiquidationIncentive ?? 0n,
    homoLiquidationIncentive: meta.homoLiquidationIncentive ?? 0n,
    sutokenLiquidationIncentive: meta.sutokenLiquidationIncentive ?? 0n,
    // no pausedActions bitmap in Sumer — default to not paused
    pausedActions: {
      [CAction.MINT]: false,
      [CAction.REDEEM]: false,
      [CAction.BORROW]: false,
      [CAction.REPAY]: false,
      [CAction.SEIZE]: false,
      [CAction.LIQUIDATE]: false,
      [CAction.TRANSFER]: false,
      [CAction.ENTER_MARKET]: false,
      [CAction.EXIT_MARKET]: false,
    },
    // borrow is allowed if listed (no explicit pause field)
    isBorrowAllowed: meta.isListed ?? true,
    // Sumer's JumpRateModelV2 stores per-second rates (not per-block)
    depositRate:
      apyToApr(
        calculateRateForCompoundType(
          (meta.supplyRatePerBlock ?? 0n).toString(),
          token.chainId,
          TimeScale.SECOND,
        ) / 100,
      ) * 100,
    variableBorrowRate:
      apyToApr(
        calculateRateForCompoundType(
          (meta.borrowRatePerBlock ?? 0n).toString(),
          token.chainId,
          TimeScale.SECOND,
        ) / 100,
      ) * 100,
  }
}

/**
 * Parse a CompactAssetGroup struct from the Sumer comptroller.
 * Guards against reverted multicall results ('0x' string placeholder).
 */
export function parseSumerAssetGroup(raw: any) {
  if (!raw || typeof raw === 'string') {
    return {
      groupId: 0,
      intraCRatePercent: 0,
      intraMintRatePercent: 0,
      intraSuRatePercent: 0,
      interCRatePercent: 0,
      interSuRatePercent: 0,
    }
  }
  return {
    groupId: Number(raw.groupId ?? raw[0] ?? 0) || 0,
    intraCRatePercent: Number(raw.intraCRatePercent ?? raw[1] ?? 0) || 0,
    intraMintRatePercent: Number(raw.intraMintRatePercent ?? raw[2] ?? 0) || 0,
    intraSuRatePercent: Number(raw.intraSuRatePercent ?? raw[3] ?? 0) || 0,
    interCRatePercent: Number(raw.interCRatePercent ?? raw[4] ?? 0) || 0,
    interSuRatePercent: Number(raw.interSuRatePercent ?? raw[5] ?? 0) || 0,
  }
}

/** Number of asset groups to fetch from the Sumer comptroller */
export const SUMER_GROUP_COUNT = 5

/** Sumer stores group rates as uint16 percentages; multiply by 1e14 to get 1e18 mantissa.
 *  Dividing by 10000 converts to a decimal (0-1). */
const SUMER_PERCENT_DIVISOR = 10000

/** Convert an array of parsed Sumer asset groups into normalized rate objects (0-1 scale). */
export function normalizeSumerGroups(
  parsed: ReturnType<typeof parseSumerAssetGroup>[],
) {
  return parsed.map((g) => ({
    groupId: g.groupId,
    intraCRate: g.intraCRatePercent / SUMER_PERCENT_DIVISOR,
    intraMintRate: g.intraMintRatePercent / SUMER_PERCENT_DIVISOR,
    intraSuRate: g.intraSuRatePercent / SUMER_PERCENT_DIVISOR,
    interCRate: g.interCRatePercent / SUMER_PERCENT_DIVISOR,
    interSuRate: g.interSuRatePercent / SUMER_PERCENT_DIVISOR,
  }))
}
